import { labelAntrian } from '../../../utils/antrian';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { konfirmasi } from '../../../utils/dialog';
import { batalkanQrisBelumBayar, PESAN_BATAL_QRIS } from '../batalQris';
import ZoomableImage from '../ZoomableImage';
import QrisKasir from '../QrisKasir';
import { usePromoKeranjang } from '../../promo/usePromoKeranjang';
import KuponPanel from '../KuponPanel';
import BagiBayarPanel from '../BagiBayarPanel';
import DpPanel from '../DpPanel';
import { bagiAwal, hitungBagi } from '../../../utils/bagiBayar';
import { dpAwal, hitungDp } from '../../../utils/uangMuka';

const bs3 = {
  btnSuccess: {
    bgcolor: '#5cb85c',
    color: '#fff',
    border: '1px solid #4cae4c',
    borderRadius: '4px',
    px: 2,
    py: 0.8,
    fontSize: '13px',
    fontWeight: 400,
    textTransform: 'none',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    '&:hover': { bgcolor: '#449d44', borderColor: '#398439' },
    '&:disabled': { bgcolor: '#5cb85c', opacity: 0.65 },
  },
  btnDefault: {
    bgcolor: '#fff',
    color: '#333',
    border: '1px solid #ccc',
    borderRadius: '4px',
    px: 2,
    py: 0.8,
    fontSize: '13px',
    fontWeight: 400,
    textTransform: 'none',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    '&:hover': { bgcolor: '#e6e6e6', borderColor: '#adadad' },
  },
  formControl: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      '& fieldset': { borderColor: '#ccc' },
      '&:hover fieldset': { borderColor: '#66afe9' },
      '&.Mui-focused fieldset': {
        borderColor: '#66afe9',
        boxShadow: 'inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px rgba(102,175,233,.6)',
      },
    },
  },
  label: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#333',
    mb: 0.5,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  alertSuccess: {
    bgcolor: '#dff0d8',
    color: '#3c763d',
    border: '1px solid #d6e9c6',
    borderRadius: '4px',
    p: 1.5,
    fontSize: '13px',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  alertDanger: {
    bgcolor: '#f2dede',
    color: '#a94442',
    border: '1px solid #ebccd1',
    borderRadius: '4px',
    p: 1.5,
    fontSize: '13px',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  alertInfo: {
    bgcolor: '#d9edf7',
    color: '#31708f',
    border: '1px solid #bce8f1',
    borderRadius: '4px',
    p: 1.5,
    fontSize: '13px',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  well: {
    bgcolor: '#f5f5f5',
    border: '1px solid #e3e3e3',
    borderRadius: '4px',
    p: 2,
    boxShadow: 'inset 0 1px 1px rgba(0,0,0,.05)',
  },
};

/**
 * PaymentDialog — Dialog konfirmasi pembayaran retail (Cash + QRIS + Non Cash).
 *
 * Props:
 *   open                   : boolean
 *   onClose                : () => void
 *   cart                   : array — daftar item di keranjang
 *   selectedPaymentMethod  : 'cash' | 'qris' | 'noncash'
 *   nonCashMethods         : array — daftar metode non cash percabang/global
 *   namaCustomer           : string
 *   noted                  : string
 *   baseUrl                : string — API base URL
 *   onSuccess              : () => void — callback setelah pembayaran berhasil
 *   onNotification         : (severity: string, message: string) => void
 */
const PaymentDialog = ({
  open,
  onClose,
  cart = [],
  selectedPaymentMethod = 'cash',
  nonCashMethods = [],
  namaCustomer = '',
  noted = '',
  baseUrl,
  onSuccess,
  onNotification,
  onPaymentCompleted,
  onPaymentSettled,
  onQrisChange,
}) => {
  const [customerCash, setCustomerCash] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle, pending, success, failed
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [orderId, setOrderId] = useState('');
  const [selectedNonCashMethodUuid, setSelectedNonCashMethodUuid] = useState('');
  const [bagi, setBagi] = useState(bagiAwal()); // pembayaran bagi (tunai + non-tunai)
  const [dp, setDp] = useState(dpAwal()); // uang muka (DP) + piutang pelanggan
  const [kodeKupon, setKodeKupon] = useState(''); // kupon yang diketik kasir (server memeriksa & menghitung)
  const [pelangganPromo, setPelangganPromo] = useState(null); // pelanggan terdaftar untuk promo ulang tahun / batas per pelanggan

  const pollingRef = useRef(null);
  const antrianQrisRef = useRef('');
  const qrisUuidRef = useRef(null); // transaksi QRIS yang sudah dibuat tetapi belum lunas/dibatalkan

  const bruto = useMemo(
    () => cart.reduce((sum, item) => sum + (parseFloat(item.harga) || 0) * item.quantity, 0),
    [cart]
  );
  // Promo otomatis (server menghitung ulang saat transaksi disimpan); subtotal = total yang harus dibayar SETELAH promo.
  const { diskonTotal: diskonPromo, promoNama, kupon, kuponGalat } = usePromoKeranjang(
    cart.map((item) => ({ baranguuid: item.baranguuid, total: (parseFloat(item.harga) || 0) * item.quantity })),
    { kodeKupon, customerUuid: pelangganPromo?.uuid }
  );
  // Isian tambahan transaksi untuk server: kupon & pelanggan (server memeriksa ulang dan menolak kupon yang tidak sah).
  const promoExtra = () => ({ ...(kodeKupon ? { kodeKupon } : {}), ...(pelangganPromo ? { customerUuid: pelangganPromo.uuid } : {}) });
  const kuponBermasalah = !!(kodeKupon && kuponGalat);
  useEffect(() => { if (!open) { setKodeKupon(''); setPelangganPromo(null); } }, [open]);
  const subtotal = Math.max(0, bruto - diskonPromo);
  // Layar pelanggan: QR QRIS tampil di jendela kedua selama pembayaran QRIS berlangsung.
  useEffect(() => {
    if (onQrisChange) onQrisChange(qrCodeUrl ? { qris: qrCodeUrl, total: subtotal } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCodeUrl, subtotal]);

  const change = useMemo(
    () => (parseFloat(customerCash || 0) || 0) - subtotal,
    [customerCash, subtotal]
  );

  const buildPayloadItems = () =>
    cart.map((item) => ({
      baranguuid: item.baranguuid,
      jumlahbarang: item.quantity,
      harga: parseFloat(item.harga) || 0,
      isSerialTracked: item.isSerialTracked || false,
      ...(item.serialNumbers && item.serialNumbers.length > 0
        ? { serialNumbers: item.serialNumbers }
        : {}),
    }));

  const buildReceiptPayload = (paymentMethod, paidAmount = 0, nomorAntrian = '', orderId = '') => ({
    nomorAntrian,
    orderId,
    items: cart.map((item) => ({
      id: item.baranguuid,
      name: item.nama,
      price: parseFloat(item.harga) || 0,
      quantity: item.quantity,
    })),
    total: subtotal,
    diskon: diskonPromo,
    promoNama,
    paymentMethod,
    namaCustomer: namaCustomer || 'Walk-in Customer',
    noted: noted || '',
    customerCash: paidAmount,
    change: paymentMethod === 'Cash' ? Math.max(0, paidAmount - subtotal) : 0,
    createdAt: new Date().toISOString(),
  });

  const handleCashPayment = async () => {
    if (subtotal <= 0) {
      onNotification('warning', 'Keranjang masih kosong.');
      return;
    }

    if ((parseFloat(customerCash || 0) || 0) < subtotal) {
      onNotification('error', 'Uang customer tidak mencukupi untuk pembayaran tunai.');
      return;
    }

    try {
      setPaymentStatus('pending');
      const respCash = await axios.post(
        `${baseUrl}/createtransaksicabang`,
        {
          pembayaran: 'cash',
          items: buildPayloadItems(),
          ...promoExtra(),
          namaCustomer: namaCustomer || null,
          noted: noted || null,
          uangCustomer: parseFloat(customerCash || 0) || 0,
        },
        { withCredentials: true }
      );
      setPaymentStatus('success');
      onNotification('success', 'Pembayaran tunai berhasil disimpan.');
      if (onPaymentCompleted) {
        onPaymentCompleted(buildReceiptPayload('Cash', parseFloat(customerCash || 0) || 0, labelAntrian(respCash.data?.data?.transaksi), respCash.data?.data?.transaksi?.order_id));
      }
      if (onPaymentSettled) onPaymentSettled();
      setCustomerCash('');
      if (onSuccess) onSuccess();
    } catch (error) {
      setPaymentStatus('idle');
      onNotification(
        'error',
        error?.response?.data?.message ||
          error?.response?.data?.msg ||
          error?.message ||
          'Gagal menyimpan transaksi retail tunai.'
      );
    }
  };

  // Pembayaran bagi: tunai + non-tunai (atau beberapa non-tunai). Server memeriksa ulang terhadap total yang dihitungnya.
  const handleBagiPayment = async () => {
    if (subtotal <= 0) {
      onNotification('warning', 'Keranjang masih kosong.');
      return;
    }
    const hasil = hitungBagi(subtotal, bagi, nonCashMethods);
    if (hasil.galat) {
      onNotification('error', hasil.galat);
      return;
    }

    try {
      setPaymentStatus('pending');
      const respBagi = await axios.post(
        `${baseUrl}/createtransaksicabang`,
        {
          pembayaranBagi: hasil.payload,
          items: buildPayloadItems(),
          ...promoExtra(),
          namaCustomer: namaCustomer || null,
          noted: noted || null,
          uangCustomer: hasil.tunaiDiterima,
        },
        { withCredentials: true }
      );
      setPaymentStatus('success');
      onNotification('success', 'Pembayaran bagi berhasil disimpan.');
      if (onPaymentCompleted) {
        onPaymentCompleted({ ...buildReceiptPayload('Split', hasil.tunaiDiterima, labelAntrian(respBagi.data?.data?.transaksi), respBagi.data?.data?.transaksi?.order_id), paymentLines: hasil.baris, change: hasil.kembalian });
      }
      if (onPaymentSettled) onPaymentSettled();
      setBagi(bagiAwal());
      if (onSuccess) onSuccess();
    } catch (error) {
      setPaymentStatus('idle');
      onNotification(
        'error',
        error?.response?.data?.message ||
          error?.response?.data?.msg ||
          error?.message ||
          'Gagal menyimpan transaksi retail bagi pembayaran.'
      );
    }
  };

  // Uang muka (DP): sebagian dibayar sekarang, sisanya jadi piutang pelanggan. Server memeriksa ulang terhadap total yang dihitungnya.
  const handleDpPayment = async () => {
    if (subtotal <= 0) {
      onNotification('warning', 'Keranjang masih kosong.');
      return;
    }
    const hasil = hitungDp(subtotal, dp, nonCashMethods);
    if (hasil.galat) {
      onNotification('error', hasil.galat);
      return;
    }

    try {
      setPaymentStatus('pending');
      const respDp = await axios.post(
        `${baseUrl}/createtransaksicabang`,
        {
          pembayaran: 'cash',
          dp: hasil.payload,
          items: buildPayloadItems(),
          ...promoExtra(),
          namaCustomer: dp.pelanggan.nama,
          customerUuid: dp.pelanggan.uuid,
          noted: noted || null,
        },
        { withCredentials: true }
      );
      setPaymentStatus('success');
      onNotification('success', `Uang muka tercatat. Sisa Rp ${Math.round(hasil.sisa).toLocaleString('id-ID')} masuk piutang ${dp.pelanggan.nama}.`);
      if (onPaymentCompleted) {
        onPaymentCompleted({
          ...buildReceiptPayload('DP', hasil.dibayar, labelAntrian(respDp.data?.data?.transaksi), respDp.data?.data?.transaksi?.order_id),
          paymentLines: hasil.baris,
          namaCustomer: dp.pelanggan.nama,
          change: 0,
        });
      }
      if (onPaymentSettled) onPaymentSettled();
      setDp(dpAwal());
      if (onSuccess) onSuccess();
    } catch (error) {
      setPaymentStatus('idle');
      onNotification(
        'error',
        error?.response?.data?.message ||
          error?.response?.data?.msg ||
          error?.message ||
          'Gagal menyimpan transaksi retail uang muka.'
      );
    }
  };

  const startPaymentPolling = (currentOrderId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    let pollCount = 0;

    pollingRef.current = setInterval(async () => {
      pollCount += 1;
      try {
        const statusResponse = await axios.get(
          `${baseUrl}/gettransaksinotification/${currentOrderId}`,
          { withCredentials: true }
        );
        const status = statusResponse.data?.data?.transaksi?.status_pembayaran;

        if (status === 'settlement') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          qrisUuidRef.current = null;
          setPaymentStatus('success');
          onNotification('success', 'Pembayaran QRIS retail berhasil.');
          if (onPaymentCompleted) {
            onPaymentCompleted(buildReceiptPayload('Qris', 0, antrianQrisRef.current, currentOrderId));
          }
          if (onPaymentSettled) onPaymentSettled();
          if (onSuccess) onSuccess();
        } else if (['cancel', 'cancelled', 'expire', 'failure'].includes(status)) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          qrisUuidRef.current = null;
          setPaymentStatus('failed');
          onNotification('error', 'Pembayaran QRIS gagal atau dibatalkan. Silakan ulangi atau ganti ke tunai.');
        }
      } catch (error) {
        if (pollCount > 3) {
          onNotification('error', 'Gagal memeriksa status pembayaran QRIS. Coba lagi beberapa saat.');
        }
      }

      if (pollCount >= 60) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setPaymentStatus('failed');
        // Tidak dibayar dalam 3 menit: transaksi QRIS dibatalkan otomatis supaya tidak tertinggal menunggu bayar.
        batalkanQrisBelumBayar(baseUrl, qrisUuidRef.current).then(({ hasil }) => {
          if (hasil === 'dibatalkan') {
            qrisUuidRef.current = null;
            setQrCodeUrl('');
            setOrderId('');
            onNotification('warning', 'QRIS tidak dibayar dan dibatalkan. Pesanan masih di keranjang; pilih metode pembayaran lagi.');
          } else if (hasil === 'dibayar') {
            setPaymentStatus('pending');
            startPaymentPolling(currentOrderId);
          } else {
            onNotification('error', 'Pembayaran QRIS timeout. Tutup dialog ini untuk membatalkannya.');
          }
        });
      }
    }, 3000);
  };

  const handleQrisPayment = async () => {
    if (subtotal <= 0) {
      onNotification('warning', 'Keranjang masih kosong.');
      return;
    }

    // Sisa QRIS sebelumnya yang belum dibatalkan (mis. pembatalan gagal): bereskan dulu supaya tidak ada transaksi ganda.
    if (qrisUuidRef.current) {
      const { hasil, pesan } = await batalkanQrisBelumBayar(baseUrl, qrisUuidRef.current);
      if (hasil === 'gagal') { onNotification('error', pesan); return; }
      if (hasil === 'dibayar') { onNotification('info', 'QRIS sebelumnya ternyata sudah dibayar. Menyelesaikan transaksi…'); setPaymentStatus('pending'); startPaymentPolling(orderId); return; }
      qrisUuidRef.current = null;
    }

    try {
      setPaymentStatus('pending');
      const response = await axios.post(
        `${baseUrl}/createtransaksicabang`,
        {
          pembayaran: 'qris',
          items: buildPayloadItems(),
          ...promoExtra(),
          namaCustomer: namaCustomer || null,
          noted: noted || null,
        },
        { withCredentials: true }
      );

      const qrisData = response.data?.data?.qris_data;
      const transaksi = response.data?.data?.transaksi;
      antrianQrisRef.current = labelAntrian(transaksi);
      const generatedImageUrl = qrisData?.generated_image_url;
      const currentOrderId = transaksi?.order_id;

      if ((!generatedImageUrl && !qrisData?.qr_string) || !currentOrderId) {
        setPaymentStatus('failed');
        onNotification('error', 'Data QRIS tidak lengkap dari payment gateway.');
        return;
      }

      // qrCodeUrl berisi data QRIS (gambar Sandbox / qr_string / mode), ditampilkan lewat QrisKasir.
      setQrCodeUrl({ url: generatedImageUrl, qrString: qrisData?.qr_string, isProduction: !!qrisData?.isProduction });
      setOrderId(currentOrderId);
      qrisUuidRef.current = transaksi?.uuid || null;
      startPaymentPolling(currentOrderId);
    } catch (error) {
      setPaymentStatus('failed');
      onNotification(
        'error',
        error?.response?.data?.message ||
          error?.response?.data?.msg ||
          error?.message ||
          'Payment gateway QRIS gagal diproses.'
      );
    }
  };

  const handleNonCashPayment = async () => {
    const selectedMethod = nonCashMethods.find((m) => m.uuid === selectedNonCashMethodUuid);
    if (!selectedMethod) {
      onNotification('error', 'Pilih metode non cash terlebih dahulu.');
      return;
    }
    if (subtotal <= 0) {
      onNotification('warning', 'Keranjang masih kosong.');
      return;
    }

    try {
      setPaymentStatus('pending');
      const respNon = await axios.post(
        `${baseUrl}/createtransaksicabang`,
        {
          pembayaran: 'noncash',
          nonCashMethodName: selectedMethod.name,
          items: buildPayloadItems(),
          ...promoExtra(),
          namaCustomer: namaCustomer || null,
          noted: noted || null,
        },
        { withCredentials: true }
      );
      setPaymentStatus('success');
      onNotification('success', 'Pembayaran non cash berhasil disimpan.');
      if (onPaymentCompleted) {
        onPaymentCompleted(buildReceiptPayload('Non Cash', 0, labelAntrian(respNon.data?.data?.transaksi), respNon.data?.data?.transaksi?.order_id));
      }
      if (onPaymentSettled) onPaymentSettled();
      setSelectedNonCashMethodUuid('');
      if (onSuccess) onSuccess();
    } catch (error) {
      setPaymentStatus('idle');
      onNotification(
        'error',
        error?.response?.data?.message ||
          error?.response?.data?.msg ||
          error?.message ||
          'Gagal menyimpan transaksi retail non cash.'
      );
    }
  };

  const handleSubmitPayment = async () => {
    if (selectedPaymentMethod === 'cash') {
      await handleCashPayment();
      return;
    }
    if (selectedPaymentMethod === 'noncash') {
      await handleNonCashPayment();
      return;
    }
    if (selectedPaymentMethod === 'bagi') {
      await handleBagiPayment();
      return;
    }
    if (selectedPaymentMethod === 'dp') {
      await handleDpPayment();
      return;
    }
    await handleQrisPayment();
  };

  const handleClose = async () => {
    // QRIS sudah tampil tetapi belum dibayar: tanya, lalu batalkan transaksinya (pesanan tetap di keranjang).
    if (qrisUuidRef.current && paymentStatus !== 'success') {
      if (!(await konfirmasi(PESAN_BATAL_QRIS, { tombolYa: 'Batalkan QRIS', bahaya: true }))) return;
      const uuid = qrisUuidRef.current;
      if (!uuid) return; // sudah lunas selagi pertanyaan tampil
      const { hasil, pesan } = await batalkanQrisBelumBayar(baseUrl, uuid);
      if (hasil === 'gagal') { onNotification('error', pesan); return; }
      if (hasil === 'dibayar') {
        onNotification('info', 'QRIS ternyata sudah dibayar pelanggan. Menyelesaikan transaksi…');
        if (!pollingRef.current) { setPaymentStatus('pending'); startPaymentPolling(orderId); }
        return;
      }
      qrisUuidRef.current = null;
      onNotification('info', 'QRIS dibatalkan. Pesanan masih di keranjang.');
    } else if (paymentStatus === 'pending' && !qrCodeUrl) {
      return; // transaksi sedang dibuat
    }
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setPaymentStatus('idle');
    setQrCodeUrl('');
    setOrderId('');
    setCustomerCash('');
    setSelectedNonCashMethodUuid('');
    setBagi(bagiAwal());
    setDp(dpAwal());
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          bgcolor: '#337ab7',
          color: '#fff',
          fontSize: '15px',
          fontWeight: 700,
          fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        }}
      >
        Konfirmasi Pembayaran Retail
      </DialogTitle>

      <DialogContent sx={{ pt: '16px !important' }}>
        {/* Total */}
        <Box
          sx={{
            ...bs3.well,
            mb: 2,
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              color: '#777',
              fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
            }}
          >
            TOTAL PEMBAYARAN
          </Typography>
          <Typography
            sx={{
              fontSize: '28px',
              fontWeight: 700,
              color: '#337ab7',
              fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
            }}
          >
            Rp {subtotal.toLocaleString('id-ID')}
          </Typography>
          {diskonPromo > 0 && (
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#2e7d32', mt: 0.5 }}>
              Promo{promoNama ? `: ${promoNama}` : ''} (- Rp {diskonPromo.toLocaleString('id-ID')} dari Rp {bruto.toLocaleString('id-ID')})
            </Typography>
          )}
        </Box>

        <KuponPanel
          kodeTerpasang={kodeKupon} onPasang={setKodeKupon} kupon={kupon} galat={kuponGalat}
          pelanggan={pelangganPromo} onPelanggan={setPelangganPromo} tampilPelanggan
          disabled={paymentStatus === 'pending'} sx={{ mb: 2 }}
        />

        {/* Cash input */}
        {selectedPaymentMethod === 'cash' && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={bs3.label}>Uang Customer</Typography>
            <TextField
              fullWidth
              type="number"
              size="small"
              placeholder="Masukkan jumlah uang..."
              value={customerCash}
              onChange={(e) => setCustomerCash(e.target.value)}
              sx={bs3.formControl}
            />
          </Box>
        )}

        {/* Change calculation */}
        {selectedPaymentMethod === 'cash' && customerCash && (
          <Box sx={change >= 0 ? bs3.alertSuccess : bs3.alertDanger}>
            <strong>
              {change >= 0
                ? `Kembalian: Rp ${change.toLocaleString('id-ID')}`
                : `Uang kurang: Rp ${Math.abs(change).toLocaleString('id-ID')}`}
            </strong>
          </Box>
        )}

        {/* Non Cash method selection */}
        {selectedPaymentMethod === 'noncash' && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={bs3.label}>Pilih Metode</Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedNonCashMethodUuid}
              onChange={(e) => setSelectedNonCashMethodUuid(e.target.value)}
              sx={bs3.formControl}
            >
              {nonCashMethods.length === 0 && (
                <MenuItem value="" disabled>Belum ada metode, hubungi admin</MenuItem>
              )}
              {nonCashMethods.map((m) => (
                <MenuItem key={m.uuid} value={m.uuid}>{m.name}</MenuItem>
              ))}
            </TextField>
            {(() => {
              const selectedMethod = nonCashMethods.find((m) => m.uuid === selectedNonCashMethodUuid);
              return selectedMethod?.imagePath ? (
                <Box sx={{ mt: 1.5 }}>
                  <ZoomableImage
                    src={`${baseUrl}/uploads/${selectedMethod.imagePath}`}
                    alt={selectedMethod.name}
                  />
                </Box>
              ) : null;
            })()}
          </Box>
        )}

        {/* Pembayaran bagi (tunai + non-tunai) */}
        {selectedPaymentMethod === 'bagi' && (
          <BagiBayarPanel
            total={subtotal}
            nonCashMethods={nonCashMethods}
            state={bagi}
            onChange={setBagi}
            disabled={paymentStatus === 'pending'}
            sx={{ mb: 2 }}
          />
        )}

        {/* Uang muka (DP) + piutang pelanggan */}
        {selectedPaymentMethod === 'dp' && (
          <DpPanel
            total={subtotal}
            nonCashMethods={nonCashMethods}
            state={dp}
            onChange={setDp}
            disabled={paymentStatus === 'pending'}
            sx={{ mb: 2 }}
          />
        )}

        {/* QRIS QR code */}
        {selectedPaymentMethod === 'qris' && qrCodeUrl && (
          <Box sx={{ py: 2 }}>
            <QrisKasir qris={qrCodeUrl} orderId={orderId} />
          </Box>
        )}

        {/* Status indicators */}
        {paymentStatus === 'pending' && (
          <Box sx={{ ...bs3.alertInfo, mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <span>Menunggu konfirmasi pembayaran...</span>
          </Box>
        )}
        {paymentStatus === 'failed' && (
          <Box sx={{ ...bs3.alertDanger, mt: 2 }}>
            Pembayaran belum berhasil. Silakan ulangi atau pilih metode pembayaran lain.
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #ddd', p: 1.5 }}>
        <Button onClick={handleClose} sx={bs3.btnDefault} disabled={paymentStatus === 'pending' && !qrCodeUrl}>
          Tutup
        </Button>
        <Button
          onClick={handleSubmitPayment}
          disabled={
            paymentStatus === 'pending' ||
            paymentStatus === 'success' ||
            kuponBermasalah ||
            (selectedPaymentMethod === 'noncash' && !selectedNonCashMethodUuid) ||
            (selectedPaymentMethod === 'bagi' && !hitungBagi(subtotal, bagi, nonCashMethods).sah) ||
            (selectedPaymentMethod === 'dp' && !hitungDp(subtotal, dp, nonCashMethods).sah)
          }
          sx={bs3.btnSuccess}
        >
          {paymentStatus === 'pending' ? 'Memproses...' : 'Bayar Sekarang'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
